<template>
  <div class="modal fade dq-admin-leave-modal" id="admin_apply_leave_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-lg dq-admin-leave-dialog">
      <div class="modal-content dq-modal-content">
        <form @submit.prevent="submit">
          <div class="modal-header dq-modal-header">
            <div class="dq-titlewrap">
              <div class="dq-hero dq-hero-success">
                <i class="ti ti-calendar-plus"></i>
              </div>
              <div>
                <h5 class="modal-title dq-title mb-0">Apply Leave for Employee</h5>
                <div class="dq-subtitle">Create a leave request on behalf of an employee.</div>
              </div>
            </div>
            <button type="button" class="btn-close dq-close" data-bs-dismiss="modal" aria-label="Close" />
          </div>

          <div class="modal-body dq-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label dq-label">Employee</label>
                <select v-model.number="form.employee_id" class="form-select dq-input" required @change="onEmployeeChange">
                  <option :value="null">Select employee</option>
                  <option v-for="e in employees" :key="e.id" :value="e.id">
                    {{ employeeLabel(e) }}
                  </option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label dq-label">Leave Type</label>
                <select v-model.number="form.leave_type_id" class="form-select dq-input" required>
                  <option :value="null">Select leave type</option>
                  <option v-for="t in typeOptions" :key="t.leave_type_id" :value="t.leave_type_id">
                    {{ t.name }} (remaining {{ t.remaining }})
                  </option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label dq-label">From Date</label>
                <input v-model="form.from_date" type="date" class="form-control dq-input" required />
              </div>
              <div class="col-md-6">
                <label class="form-label dq-label">To Date</label>
                <input
                  v-model="form.to_date"
                  type="date"
                  class="form-control dq-input"
                  required
                  :disabled="form.half_day"
                />
              </div>
              <div class="col-12">
                <div class="form-check">
                  <input id="admin_half_day" v-model="form.half_day" class="form-check-input" type="checkbox" @change="onHalfDayToggle" />
                  <label class="form-check-label" for="admin_half_day">Half-day leave (same from/to date)</label>
                </div>
              </div>
              <div class="col-12">
                <label class="form-label dq-label">Reason</label>
                <textarea v-model.trim="form.reason" class="form-control dq-input" rows="3" placeholder="Optional reason..." />
              </div>
              <div class="col-12">
                <div class="form-check">
                  <input id="admin_auto_approve" v-model="form.auto_approve" class="form-check-input" type="checkbox" v-can="'Leaves.Approve'" />
                  <label class="form-check-label" for="admin_auto_approve">Approve immediately</label>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-footer">
            <button class="btn btn-light dq-btn dq-btn-muted" type="button" data-bs-dismiss="modal">Cancel</button>
            <button class="btn btn-primary dq-btn" type="submit" :disabled="store.loading">
              <span v-if="store.loading" class="spinner-border spinner-border-sm me-2"></span>
              Apply Leave
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import * as bootstrap from "bootstrap";
import { useAdminLeaveStore } from "@/stores/adminLeaveStore";
import { useEmployeeStore } from "@/stores/employeeStore";
import { toast } from "@/utils/toast";

const store = useAdminLeaveStore();
const empStore = useEmployeeStore();
const typeOptions = ref([]);

const form = reactive({
  employee_id: null,
  leave_type_id: null,
  from_date: "",
  to_date: "",
  half_day: false,
  auto_approve: false,
  reason: "",
});

const employees = computed(() => empStore.employees || []);

function employeeLabel(e) {
  const name = [e.first_name, e.last_name].filter(Boolean).join(" ").trim();
  return name || e.email || `Employee #${e.id}`;
}

function reset() {
  form.employee_id = null;
  form.leave_type_id = null;
  form.from_date = "";
  form.to_date = "";
  form.half_day = false;
  form.auto_approve = false;
  form.reason = "";
  typeOptions.value = [];
}

function onHalfDayToggle() {
  if (form.half_day && form.from_date) {
    form.to_date = form.from_date;
  }
}

async function onEmployeeChange() {
  form.leave_type_id = null;
  typeOptions.value = [];
  if (!form.employee_id) return;
  typeOptions.value = await store.fetchEmployeeSummary(form.employee_id);
}

async function submit() {
  if (!form.employee_id || !form.leave_type_id || !form.from_date || !form.to_date) {
    return toast("Please complete all required fields", "error");
  }
  if (form.half_day) {
    form.to_date = form.from_date;
  }
  await store.create({
    employee_id: form.employee_id,
    leave_type_id: form.leave_type_id,
    from_date: form.from_date,
    to_date: form.to_date,
    half_day: form.half_day,
    auto_approve: form.auto_approve,
    reason: form.reason || null,
  });
  bootstrap.Modal.getInstance(document.getElementById("admin_apply_leave_modal"))?.hide();
  reset();
}

function open() {
  bootstrap.Modal.getOrCreateInstance(document.getElementById("admin_apply_leave_modal")).show();
}

onMounted(async () => {
  if (!employees.value.length) {
    await empStore.fetchEmployees({ per_page: 500 });
  }
});

defineExpose({ open });
</script>

<style scoped>
.dq-admin-leave-dialog {
  max-width: 760px;
}
.dq-modal-content {
  border-radius: 22px;
  border: none;
}
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(34, 197, 94, 0.1), #fff);
}
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-hero {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dq-hero-success {
  background: rgba(34, 197, 94, 0.16);
  color: rgb(22, 163, 74);
}
.dq-body {
  padding: 18px 22px;
}
.dq-footer {
  padding: 14px 22px 18px;
}
</style>
